import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ProjectArchiveBucketJob,
  ProjectArchiveJob,
} from '../../../../../../typings/api/skymap/rest/v1/project';
import { formattedDate } from '../../../../utils/dateUtils';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { InfoBox } from '../../info-box/info-box';
import { RestoreProjectDialog } from '../dialogs/restore-project-dialog';
import { BucketsTable, BucketsTableItem } from './buckets-table';
import { Flex } from './styles';

interface Props {
  job: ProjectArchiveJob;
}

const ArchiveInfoBox = (props: Props) => {
  const { t } = useTranslation();
  const { reloadProject } = React.useContext(ProjectContext);
  const [restoreProjectDialogVisible, setRestoreProjectDialogVisible] = React.useState(false);

  const restoreButton = (
    <Button
      color="primary"
      variant="contained"
      onClick={() => {
        setRestoreProjectDialogVisible(true);
      }}
    >
      {t('projectSettings.status.restoreButton', { ns: 'components' })}
    </Button>
  );

  const resolveIcon = (bucketJob: ProjectArchiveBucketJob): BucketsTableItem['state'] => {
    switch (bucketJob.state) {
      case 'CHECK_INVENTORY_REPORT':
      case 'CREATE_INVENTORY_CONFIGURATION':
      case 'CREATE_LIFECYCLE_RULE':
      case 'DELETE_INVENTORY_CONFIGURATION':
      case 'DELETE_LIFECYCLE_RULE':
        return bucketJob.errorMessage
          ? {
              icon: {
                name: 'exclamation-circle',
                color: 'crimson',
                title: t('projectSettings.status.archiveStates.unexpectedError', {
                  ns: 'components',
                }),
              },
            }
          : {
              icon: {
                name: 'spinner',
                spin: true,
              },
            };

      case 'DONE':
        return {
          icon: {
            name: 'check',
            color: 'green',
            title: t('projectSettings.status.archiveStates.allFilesArchived', {
              ns: 'components',
            }),
          },
        };

      case 'IDLE':
        return t('projectSettings.status.archiveStates.notStarted', { ns: 'components' });
    }
  };

  const getTitle = () => {
    switch (props.job.status) {
      case 'DONE':
        return t('projectSettings.status.archiveStates.projectArchived', { ns: 'components' });

      case 'IDLE':
        return (
          <>
            {t('projectSettings.status.archiveStates.projectInactive', { ns: 'components' })}
            <br />
            {t('projectSettings.status.archiveStates.awaitingArchiving', {
              ns: 'components',
              date: formattedDate(props.job.archiveDate),
            })}
          </>
        );
      case 'IN_PROGRESS':
        return t('projectSettings.status.archiveStates.archivingInProgress', {
          ns: 'components',
        });

      case 'ERROR':
        return t('projectSettings.status.archiveStates.checkS3Status', { ns: 'components' });
    }
  };

  const renderContent = () => {
    return (
      <div>
        <span>{getTitle()}</span>

        {props.job.bucketJobs.length > 0 && (
          <BucketsTable
            buckets={props.job.bucketJobs.map((x) => ({
              id: x.id,
              name: x.s3BucketName,
              state: resolveIcon(x),
            }))}
          />
        )}
      </div>
    );
  };

  const canProjectBeRestored = props.job && ['IDLE', 'DONE'].includes(props.job.status);

  return (
    <Component>
      <InfoBox bottomMargin={true} color="yellow" leftIcon={{ icon: ['fad', 'lock-alt'] }}>
        <Flex>
          {renderContent()}
          {canProjectBeRestored && restoreButton}
        </Flex>
      </InfoBox>

      {restoreProjectDialogVisible && (
        <RestoreProjectDialog
          latestArchiveJob={props.job}
          onClose={(activated) => {
            setRestoreProjectDialogVisible(false);

            if (activated) {
              void reloadProject();
            }
          }}
        />
      )}
    </Component>
  );
};

const Component = styled.div``;

export { ArchiveInfoBox };
